import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Instagram, ExternalLink, Star, Utensils } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const InfluencerDetail = () => {
  const { id } = useParams();
  const [inf, setInf] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [restaurants, setRestaurants] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get(`/influencers/${id}`),
      api.get(`/reviews?influencer_id=${id}`),
      api.get(`/influencers/${id}/restaurants`),
    ]).then(([a, b, c]) => {
      setInf(a.data);
      setReviews(b.data);
      setRestaurants(c.data);
    }).catch(console.error);
  }, [id]);

  if (!inf) return <div className="text-center py-20 text-muted-foreground">Memuat...</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12" data-testid="influencer-detail">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 items-start">
        <div className="md:col-span-1">
          <div className="aspect-square rounded-3xl overflow-hidden border-4 border-primary/20">
            <img src={inf.picture} alt={inf.name} className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-2">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Influencer</p>
          <h1 className="font-heading text-5xl font-black mt-2 leading-none">{inf.name}</h1>
          <a
            href={`https://instagram.com/${inf.instagram_handle?.replace("@", "")}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-primary font-semibold hover:underline"
            data-testid="ig-profile-link"
          >
            <Instagram className="w-5 h-5" /> {inf.instagram_handle} <ExternalLink className="w-4 h-4" />
          </a>
          <p className="mt-4 text-foreground/80 leading-relaxed">{inf.bio}</p>
          <div className="flex gap-3 mt-6">
            <Badge variant="secondary" className="py-2 px-4">{inf.review_count} Reviews</Badge>
            <Badge variant="secondary" className="py-2 px-4">{restaurants.length} Restoran direview</Badge>
          </div>
        </div>
      </div>

      {/* Reviewed Restaurants */}
      {restaurants.length > 0 && (
        <section className="mb-12" data-testid="reviewed-restaurants">
          <h2 className="font-heading text-2xl font-black mb-4 flex items-center gap-2">
            <Utensils className="w-5 h-5 text-primary" /> Restoran yang Mereka Review
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {restaurants.map(r => (
              <Link key={r.id} to={`/restaurants/${r.slug}`} className="warm-card group" data-testid={`reviewed-rest-${r.slug}`}>
                <div className="aspect-square overflow-hidden bg-muted">
                  <img src={r.cover_image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <h3 className="font-heading font-bold text-xs leading-tight line-clamp-2">{r.name}</h3>
                  <div className="flex items-center gap-1 mt-1 text-[10px]">
                    <Star className="w-2.5 h-2.5 fill-primary text-primary" />
                    <span className="font-semibold">{r.avg_rating?.toFixed(1) || "—"}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <h2 className="font-heading text-3xl font-black mb-6">Karya Review</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="influencer-reviews">
        {reviews.map(rv => (
          <div key={rv.id} className="warm-card overflow-hidden" data-testid={`review-${rv.id}`}>
            <Link to={`/restaurants/${rv.restaurant?.slug}`}>
              <div className="aspect-video overflow-hidden bg-muted relative">
                <img src={rv.cover_image || rv.restaurant?.cover_image} alt="" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" /> {rv.overall.toFixed(1)}
                </div>
              </div>
            </Link>
            <div className="p-5">
              <Link to={`/restaurants/${rv.restaurant?.slug}`} className="text-xs text-muted-foreground hover:text-primary">
                → {rv.restaurant?.name}
              </Link>
              <h3 className="font-heading font-bold text-lg leading-tight mt-1">{rv.title}</h3>
              {rv.content && <p className="text-sm text-foreground/70 mt-2 line-clamp-2">{rv.content}</p>}
              <div className="grid grid-cols-4 gap-2 mt-4 text-center text-xs border-t border-border pt-3">
                <div><div className="text-muted-foreground uppercase tracking-wider">Rasa</div><div className="font-bold text-primary">{rv.rasa}</div></div>
                <div><div className="text-muted-foreground uppercase tracking-wider">Harga</div><div className="font-bold text-primary">{rv.harga}</div></div>
                <div><div className="text-muted-foreground uppercase tracking-wider">Amb.</div><div className="font-bold text-primary">{rv.ambience}</div></div>
                <div><div className="text-muted-foreground uppercase tracking-wider">Score</div><div className="font-bold text-primary">{rv.overall}</div></div>
              </div>
              {rv.instagram_video_url && (
                <a href={rv.instagram_video_url} target="_blank" rel="noreferrer" data-testid={`ig-link-${rv.id}`}>
                  <Button variant="outline" size="sm" className="w-full mt-4 rounded-full">
                    <Instagram className="w-4 h-4 mr-2" /> Tonton Video Review
                  </Button>
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InfluencerDetail;
